import React, { useRef } from 'react';
import { isString } from 'mathjs';
import TextUtils from '../../classes/utilities/TextUtils';
import ContextMenu from '../menus/ContextMenu';
import { IconFillStyles, ResetIcon, WarningTriangleIcon } from '../Iconset';
import { TooltipKeys } from "../tooltips/TooltipKeys";
import { IContextMenuItem } from '../../interfaces/Misc';
import Spacer from '../layout/Spacer';

export interface IBasePropertyProps
{
	/** the text to display next to the property itself. this text will be formatted for display using Utils.prettifyText function, unless useRawLabel is also set to true */
	label?: React.ReactNode,
	/** the default value to reset the property to when the user holds CTRL and double-clicks the label, or uses the context menu */
	defaultValue?: any,
	/** same as defaultValue, but as an array */
	defaultValues?: any[],
	/** the current value of the property; used for comparison against defaultValue(s) */
	value?: any,
	/** usually used when the property controls multiple targets, each with a different value. 
	 * when set to true, the property will display a special mark to communicate this situation to the user
	 */
	multivalues?: boolean,
	/** this function will get called whenever the internal value of the property is changed, but not finalized */
	onChangeCallback?: (value: any) => void,
	/** this function will get called whenever the internal value of the property is changed and finalized */
	onCommitCallback?: (value: any) => void,
	/** when set to true, it means this property has an externally-controlled value it compares to */
	synced?: boolean,
	/** determines the state of a synchronized property compared to the external control value */
	syncStatus?: string,
	/** when set to true, a small colored bar will be rendered to visually display the syncStatus */
	showSyncStatus?: boolean,
	/** when set to true, a small button will be rendered that when clicked, will set the property's value to the external control value */
	showResetButton?: boolean,
	/** when set to true, the property will render any input fields as separate rows instead of a single row */
	renderChildrenInSeparateRows?: boolean,
	/** a label to draw above the input field(s) instead of next to them */
	labelAboveProperty?: React.ReactNode,
	/** a label to draw after (to the right of) the input field(s) instead of before (to the left of) them */
	labelAfterProperty?: React.ReactNode,
	/** added styling for the property's label */
	labelStyle?: React.CSSProperties,
	/** added css class names to the property's label */
	labelClass?: string,
	/** added css class names to the property's wrapper table */
	className?: string,
	/** styling of the property's wrapper table */
	style?: React.CSSProperties,
	/** when set to true, the property will display a highlight outline around itself via setting a CSS attribute */
	highlight?: boolean,
	/** identifies which tooltip content to display when hovering the property */
	tooltip?: string,
	/** same as `tooltip` but more modern */
	["data-tooltip"]?: string,
	/** used for automation purposes to specifically identify this property */
	["data-name"]?: string,
	/** when set, a warning icon will be displayed next to the property that, when hovered, will display the value of this prop as a warning that something is wrong */
	warning?: string,
	/** additional elements to display after the property's label */
	addons?: React.ReactNode,
	/** the property is used for display only, and the user will not be able to set its value */
	readOnly?: boolean,
	/** when set to true, the property will be completely disabled and disregard user input. it will also be visually different to hint this state */
	disabled?: boolean,
	/** when set to true, the property will display additional text after the label that signals the user that it must have a value set */
	required?: boolean,
	/** context menu to display instead of the default */
	contextMenuOverride?: IContextMenuItem[],
	/** content menu items to display in addition to the default */
	contextMenuAddons?: IContextMenuItem[],
	/** when set to `true`, the context menu event will be ignored */
	contextMenuDisabled?: boolean,
	/** when set to true, the property render without label and with minimal styling; usually set when the property is used as part of another visual component */
	decoratorMode?: boolean,
	/** when set to true, the property's label will not be formatted for display using Utils.prettifyText function */
	useRawLabel?: boolean,
	/** custom function to use when resetting the property to its default value instead of the default one */
	resetFunc?: () => void,
	buttons?: React.ReactNode[],
}

export default function PropertyBase(props: React.PropsWithChildren<IBasePropertyProps>)
{
	const toolTipRef = useRef<HTMLSpanElement>(null);
	const resetFunc = props.resetFunc ?? ResetToDefaultValue;
	const displayLabel = (props.useRawLabel || !isString(props.label)) ? props.label : TextUtils.prettifyText(props.label ?? "", { capitalize: true });
	const displayLabelAbove = (props.useRawLabel || !isString(props.labelAboveProperty)) ? props.labelAboveProperty : TextUtils.prettifyText(props.labelAboveProperty ?? "", { capitalize: true });

	function onMouseEnterWarning(e)
	{
		if (toolTipRef && toolTipRef.current)
		{
			if (e)
			{
				const toolTipSize = toolTipRef.current.getBoundingClientRect();
				const iconSize = e.target.getBoundingClientRect();

				let xModifier = 0;
				let yModifier = (toolTipSize.height*-1) - 8;

				if(window.innerWidth - iconSize.x < toolTipSize.width + xModifier) xModifier -= (toolTipSize.width);
				if(window.innerHeight - iconSize.y < toolTipSize.height + yModifier) yModifier -= (toolTipSize.height + 15);

				const x = (iconSize.x + xModifier) + 'px', y = (iconSize.y + yModifier) + 'px';

				toolTipRef.current.style.top = y;
				toolTipRef.current.style.left = x;

				toolTipRef.current.style.visibility = 'visible';
			}
		}
	}

	function onMouseExitWarning(e)
	{
		if (toolTipRef && toolTipRef.current)
		{
			if (e)
			{
				toolTipRef.current.style.visibility = 'hidden';
			}
		}
	}

	function ResetToDefaultValue()
	{
		if (props.defaultValue === undefined && props.defaultValues === undefined)
			return;

		//console.log("resetting " + props.label + " to default value..");
		if (props.onChangeCallback)
		{
			props.onChangeCallback(props.defaultValue ?? props.defaultValues);
		}

		if (props.onCommitCallback)
		{
			props.onCommitCallback(props.defaultValue ?? props.defaultValues);
		}
	}

	const arRows = new Array<JSX.Element>();
	let k = 0;

	if (props.labelAboveProperty)
	{
		arRows.push(
			<tr key={k++}>
				<td data-name={props["data-name"] ?? "property-" + props.labelAboveProperty + "-label"}
					className={["propertyLabel", (props.labelClass ?? "")].join(' ')}
					style={props.labelStyle ?? {}}
					data-status={props.defaultValue !== undefined && props.value !== props.defaultValue ? "modified" : ""}
					onDoubleClick={(e) =>
					{
						if (e.ctrlKey || e.metaKey)
							ResetToDefaultValue();
					}}
					data-tooltip={props.tooltip || props["data-tooltip"]}
				>
					{displayLabelAbove}
				</td>
			</tr>
		)
	}

	if (props.renderChildrenInSeparateRows && props.children && React.Children.count(props.children) > 0)
	{
		arRows.push((
			<tr key={k++}>
				{
					!props.label ? null :
						<td data-name={props["data-name"] ?? "property-" + props.label + "-label"}
							className={["propertyLabel", (props.labelClass ?? "")].join(' ')}
							style={props.labelStyle ?? {}}
							data-status={props.defaultValue !== undefined && props.value !== props.defaultValue ? "modified" : ""}
							onDoubleClick={(e) =>
							{
								if (e.ctrlKey || e.metaKey)
									ResetToDefaultValue();
							}}
							data-tooltip={props.tooltip || props["data-tooltip"]}
						>
							<div className={"propertyLabelWrapper"}>
								{displayLabel}
								{props.addons}
							</div>
						</td>
				}
				{
					<td className={["propertyRow", props.children[0].props.cellclass || props.children[0].props["data-cellclass"]].join(' ')}>
						{props.children[0]}
					</td>
				}
			</tr>
		));
		React.Children.map(props.children, (child: React.ReactNode, i) =>
		{
			if(i === 0) return; // we already added the first child above
			if(!React.isValidElement(child)) return;

			arRows.push((
				<tr key={k++}>
					{
						!props.label ? null :
							<td>
								&nbsp;
							</td>
					}
					{
						<td className={child.props.cellclass || child.props["data-cellclass"]}>
							{child}
						</td>
					}
				</tr>
			));
		});
	}
	else
	{
		arRows.push(
			<tr key={k++}>
				{
					!props.synced ? null :
						<td className={"syncStatusZone"}>
							{
								!props.showSyncStatus ? null :
									<div className={"syncStatus"} data-status={props.syncStatus} />
							}
						</td>
				}
				{
					!props.label ? null :
						<>
							<td data-name={props["data-name"] ?? "property-" + props.label + "-label"}
								className={["propertyLabel", (props.labelClass ?? "")].join(' ')}
								style={props.labelStyle ?? {}}
								data-status={props.defaultValue !== undefined && props.value !== props.defaultValue ? "modified" : ""}
								data-readonly={props.readOnly ? "true" : "false"}
								data-disabled={props.disabled ? "true" : "false"}
								onDoubleClick={(e) =>
								{
									if (e.ctrlKey || e.metaKey)
										ResetToDefaultValue();
								}}
								data-tooltip={props.tooltip || props["data-tooltip"]}
							>
								<div className={"propertyLabelWrapper"}>
									{displayLabel}
									{props.required ? <label style={{ color: '#F04949' }}>&nbsp;(Required)</label> : <></>}
									<Spacer stretchTo={{ x: "100%" }} />
									{props.addons}
								</div>
							</td>
							<td style={{ width: "4px" }} />
						</>
				}
				{
					React.Children.map(props.children, (child: React.ReactNode, idx) =>
					{
						if (!React.isValidElement(child))
							return child;

						return (
							<td key={idx}
								className={child.props.cellclass || child.props["data-cellclass"]}
							>
								{child}
							</td>
						)
					})
				}
				{
					props.buttons && 
						<>
							<td>
								<Spacer size={{ x: "2px" }} />
							</td>
							{
								props.buttons.map((button, idx) =>
								{
									return (
										<td key={idx}
											className={"propertySmallCell"}
										>
											{button}
										</td>
									)
								})
							}
						</>
				}
				{
					!props.labelAfterProperty ? null :
						<>
							<td data-name={props["data-name"] ?? "property-" + props.labelAfterProperty + "-label"}
								className={["propertyLabel", (props.labelClass ?? "after")].join(' ')}
								data-status={props.defaultValue !== undefined && props.value !== props.defaultValue ? "modified" : ""}
								data-readonly={props.readOnly ? "true" : "false"}
								data-disabled={props.disabled ? "true" : "false"}
								onDoubleClick={(e) =>
								{
									if (e.ctrlKey || e.metaKey)
										ResetToDefaultValue();
								}}
								data-tooltip={props.tooltip || props["data-tooltip"]}
							>
								<div className={"propertyLabelWrapper"}>
									<Spacer size={{ x: "4px" }} />
									{props.labelAfterProperty}
									{props.required ? <label style={{ color: '#F04949' }}>&nbsp;(Required)</label> : <></>}
								</div>
							</td>
						</>
				}
				{
					!props.synced ? null :
						<td className={"resyncZone"}>
							{
								!props.showResetButton ? null :
									<ResetIcon onClick={resetFunc}
										data-name={props["data-name"] ? props["data-name"] + "-resync-button" : "synced-property-resync-button"}
										data-tooltip={TooltipKeys.SyncWithDefaultState}
									/>
							}
						</td>
				}
				{
					!props.warning ? null :
						<td className={"warningTriangleContainer"}>
							<WarningTriangleIcon
								onClick={null}
								draggable={true}
								onDragStart={(e)=>{e.preventDefault()}}
								className={"warningTriangleIcon"}
								onMouseEnter={onMouseEnterWarning}
								onMouseLeave={onMouseExitWarning}
								fillStyle={IconFillStyles.red}
								data-name={props["data-name"] ? props["data-name"] + "-warning-triangle-icon" : "synced-property-warning-icon"}
							/>
							<div className="tooltip cleanTooltip">
								<span className="tooltiptext" ref={toolTipRef}>{props.warning}</span>
							</div>
						</td>
				}
			</tr>
		);
	}

	return (
		<table className={["propertyField", (props.className ?? "")].join(' ')}
			data-name={props["data-name"] ?? "property-root"}
			data-highlight={props.highlight ? "true" : "false"}
			data-disabled={props["data-disabled"] ? "true" : "false"}
			style={{
				...props.style,
			}}
			onContextMenu={(e) =>
			{
				if(props.contextMenuDisabled || (props.defaultValue === undefined && props.defaultValues === undefined)) return;

				e.preventDefault();
				e.stopPropagation();
				ContextMenu.show(e, props.contextMenuOverride ? props.contextMenuOverride : [
					{
						renderObject: "Reset to default",
						id: "Reset to default",
						callback: () =>
						{
							ResetToDefaultValue();
						},
					} as IContextMenuItem,
				].concat(props.contextMenuAddons ?? [])
				);
			}}
		>
			<tbody style={{ backgroundColor: props.decoratorMode ? 'transparent' : '', }}>
				{arRows}
			</tbody>
		</table>
	);
}

export function PropertyDataName(dataName: string, className: string, suffix?: string)
{
	return (dataName ? dataName + "-" : "") + "-" + className + (suffix ? "-" + suffix : "");
}
